import './NormalBidHistory.css';
import { useState, useEffect } from 'react';

function NormalBidHistory({ onBack }) {
  const [bidHistory, setBidHistory] = useState([]);
  const [filteredBidHistory, setFilteredBidHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;
  const [normalGames, setNormalGames] = useState([]);
  const [filters, setFilters] = useState({
    gameName: '',
    gameType: '',
    bidStatus: '',
    session: ''
  });

  // Fetch bid history and game list on mount
  useEffect(() => {
    fetchNormalBidHistory();
    fetchNormalGames();
  }, []);

  // Apply filters when bidHistory or filters change
  useEffect(() => {
    applyFilters();
    setCurrentPage(1);
  }, [bidHistory, filters]);

  const fetchNormalBidHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const userData = localStorage.getItem('user');
      const user = userData ? JSON.parse(userData) : null;
      const userId = user?.id;

      if (!userId) {
        setError('User ID not found. Please login again.');
        return;
      }

      const response = await fetch(
        `https://backend.mainbazar777.site/api/bid-history/${userId}`
      );
      const result = await response.json();

      if (result.success && result.data) {
        setBidHistory(result.data);
      } else {
        setError('Failed to fetch bid history');
      }
    } catch (err) {
      setError('Error fetching bid history: ' + err.message);
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchNormalGames = async () => {
    try {
      const response = await fetch('https://backend.mainbazar777.site/api/fetch-normal-game');
      if (!response.ok) return;
      const data = await response.json();
      if (data.success && data.normal_games) {
        setNormalGames(data.normal_games.map(g => ({ id: g.id, name: g.game_name })));
      }
    } catch (err) {
      console.error('Error fetching normal games:', err);
    }
  };

  const applyFilters = () => {
    let filtered = bidHistory;

    if (filters.gameName) {
      filtered = filtered.filter(item =>
        item.bid_history.game_name === filters.gameName
      );
    }

    if (filters.gameType) {
      filtered = filtered.filter(item =>
        item.game_type_name.toLowerCase().includes(filters.gameType.toLowerCase())
      );
    }

    if (filters.bidStatus) {
      filtered = filtered.filter(item =>
        item.bid_history.bid_status.toLowerCase() === filters.bidStatus.toLowerCase()
      );
    }

    if (filters.session) {
      filtered = filtered.filter(item => {
        if (filters.session === 'none') {
          return item.bid_history.session === null;
        }
        return item.bid_history.session?.toLowerCase() === filters.session.toLowerCase();
      });
    }

    setFilteredBidHistory(filtered);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      gameName: '',
      gameType: '',
      bidStatus: '',
      session: ''
    });
  };

  const formatBidNumber = (bid, gameTypeName, session) => {
    const str = String(bid).padStart(
      gameTypeName === 'Full Sangam' ? 6 : gameTypeName === 'Half Sangam' ? 4 : 0,
      '0'
    );
    if (gameTypeName === 'Half Sangam') {
      if (session?.toLowerCase() === 'open') {
        return { left: str.slice(0, 1), right: str.slice(1, 4), leftLabel: 'Open Digit', rightLabel: 'Close Panna' };
      }
      if (session?.toLowerCase() === 'close') {
        return { left: str.slice(0, 3), right: str.slice(3, 4), leftLabel: 'Open Panna', rightLabel: 'Close Digit' };
      }
    }
    if (gameTypeName === 'Full Sangam') {
      return { left: str.slice(0, 3), right: str.slice(3, 6), leftLabel: 'Open Panna', rightLabel: 'Close Panna' };
    }
    return null;
  };

  const formatDateTime = (createdAt) => {
    const d = new Date(createdAt);
    const date = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    const time = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    return `${date} ${time}`;
  };

  const totalPages = Math.ceil(filteredBidHistory.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedBidHistory = filteredBidHistory.slice(startIndex, endIndex);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pages = new Set(
      [1, 2, currentPage - 1, currentPage, currentPage + 1, totalPages]
        .filter(p => p >= 1 && p <= totalPages)
    );
    const sorted = [...pages].sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push('...');
      result.push(sorted[i]);
    }
    return result;
  };

  return (
    <div className="normal-bid-history-page">
      {/* Header */}
      <header className="nbh-header">
        <button className="back-btn" onClick={onBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
        <h1>Normal Bid History</h1>
      </header>

      {/* Content */}
      <div className="nbh-content">
        {loading && <div className="loading">Loading bid history...</div>}
        {error && <div className="error">{error}</div>}

        {!loading && !error && (
          <>
            {/* Filters Section */}
            <div className="filters-section">
              <h4>Filters</h4>
              <div className="filters-grid">
                <div className="filter-group">
                  <label>Game Name</label>
                  <select
                    value={filters.gameName}
                    onChange={(e) => handleFilterChange('gameName', e.target.value)}
                  >
                    <option value="">All Games</option>
                    {normalGames.map(game => (
                      <option key={game.id} value={game.name}>{game.name}</option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <label>Game Type</label>
                  <select
                    value={filters.gameType}
                    onChange={(e) => handleFilterChange('gameType', e.target.value)}
                  >
                    <option value="">All Types</option>
                    <option value="Single Digit">Single Digit</option>
                    <option value="Jodi">Jodi</option>
                    <option value="Single Panna">Single Panna</option>
                    <option value="Double Panna">Double Panna</option>
                    <option value="Triple Panna">Triple Panna</option>
                    <option value="Full Sangam">Full Sangam</option>
                    <option value="Half Sangam">Half Sangam</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label>Bid Status</label>
                  <select
                    value={filters.bidStatus}
                    onChange={(e) => handleFilterChange('bidStatus', e.target.value)}
                  >
                    <option value="">All Status</option>
                    <option value="placed">Placed</option>
                    <option value="won">Won</option>
                    <option value="lost">Lost</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label>Session</label>
                  <select
                    value={filters.session}
                    onChange={(e) => handleFilterChange('session', e.target.value)}
                  >
                    <option value="">All Sessions</option>
                    <option value="open">Open</option>
                    <option value="close">Close</option>
                    <option value="none">No Session</option>
                  </select>
                </div>
              </div>
              <button className="clear-filters-btn" onClick={clearFilters}>Clear Filters</button>
            </div>

            {/* Bid History Cards */}
            <div className="bid-history-list">
              <h4>Bid History ({filteredBidHistory.length})</h4>
              {filteredBidHistory.length === 0 ? (
                <div className="no-data">
                  {bidHistory.length === 0
                    ? 'No bid history found'
                    : 'No bids match the selected filters'}
                </div>
              ) : (
                <>
                  <div className="cards-container">
                    {paginatedBidHistory.map((item, index) => {
                      const bidFmt = formatBidNumber(
                        item.bid_history.bid,
                        item.game_type_name,
                        item.bid_history.session
                      );
                      const session = item.bid_history.session;
                      return (
                        <div key={index} className="bid-card">
                          <div className="bc-left">
                            <div className="bc-meta">
                              <span className="bc-game-name">{item.bid_history.game_name}</span>
                              <span className={`bc-status status-${item.bid_history.bid_status}`}>
                                {item.bid_history.bid_status}
                              </span>
                            </div>
                            <div className="bc-sub">
                              <span className="bc-game-type">{item.game_type_name}</span>
                              {session && (
                                <>
                                  <span className="bc-dot">•</span>
                                  <span className="bc-session-text">{session}</span>
                                </>
                              )}
                            </div>
                            <div className="bc-bid">
                              {bidFmt ? (
                                <span className="bid-split">
                                  <span className="bid-group">
                                    <span className="bid-label">{bidFmt.leftLabel}</span>
                                    <span className="bid-part">{bidFmt.left}</span>
                                  </span>
                                  <span className="bid-arrow">→</span>
                                  <span className="bid-group">
                                    <span className="bid-label">{bidFmt.rightLabel}</span>
                                    <span className="bid-part">{bidFmt.right}</span>
                                  </span>
                                </span>
                              ) : (
                                <span className="bid-plain">{item.bid_history.bid}</span>
                              )}
                            </div>
                          </div>
                          <div className="bc-right">
                            <span className="bc-amount">₹{item.bid_history.amount}</span>
                            <span className="bc-date">{formatDateTime(item.bid_history.created_at)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {totalPages > 1 && (
                    <div className="pagination">
                      <button
                        className="pagination-btn"
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        ← Previous
                      </button>

                      <div className="page-numbers">
                        {getPageNumbers().map((page, i) =>
                          page === '...' ? (
                            <span key={`e-${i}`} className="page-ellipsis">...</span>
                          ) : (
                            <button
                              key={page}
                              className={`page-number ${currentPage === page ? 'active' : ''}`}
                              onClick={() => goToPage(page)}
                            >
                              {page}
                            </button>
                          )
                        )}
                      </div>

                      <button
                        className="pagination-btn"
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next →
                      </button>
                    </div>
                  )}

                  <div className="pagination-info">
                    Showing {startIndex + 1} to {Math.min(endIndex, filteredBidHistory.length)} of {filteredBidHistory.length} bids
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default NormalBidHistory;
